import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  Box,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  
  // Validate props AFTER ALL hooks
  MenuItem,
  Pagination,
  Select
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  ArrowUpward, 
  ArrowDownward, 
  Sort, 
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useAuth } from '../auth/AuthContext';

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  maxWidth: '100vw',
  '@media (max-width: 600px)': {
    minWidth: 0,
    maxWidth: '100vw',
    '& .MuiTable-root': {
      minWidth: 0,
      width: '100vw',
    },
    '& .MuiTableCell-root': {
      fontSize: '0.55rem',
      padding: '0.5px 0.5px',
      maxWidth: 28,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      height: 18,
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
      fontSize: '0.60rem',
      padding: '1px 0.5px',
      height: 16,
    },
    '& .MuiCheckbox-root': {
      padding: '0.5px',
      transform: 'scale(0.5)',
    },
    '& .MuiSvgIcon-root': {
      fontSize: '0.9em',
    },
    '& .MuiButton-root': {
      minWidth: 0,
      padding: '2px 4px',
      fontSize: '0.60rem',
    },
    '& .MuiPaginationItem-root': {
      fontSize: '0.60rem',
      minWidth: 22,
      height: 18,
    },
    '& .hide-on-mobile': {
      display: 'none',
    },
  },
  '& .MuiTable-root': {
    borderCollapse: 'separate',
    borderSpacing: 0,
    width: '100%',
    // minWidth: 600, // Remove minWidth for mobile fit
  },
  '& .MuiTableHead-root .MuiTableRow-root': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(148,163,184,0.06)' : 'rgba(15,23,42,0.03)',
    '& .MuiTableCell-root': {
      color: theme.palette.text.secondary,
      fontWeight: 700,
      fontSize: '0.7rem',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      padding: '12px 12px',
      borderBottom: `1px solid ${theme.palette.divider}`,
      background: 'inherit',
      '&:first-of-type': {
        borderTopLeftRadius: theme.shape.borderRadius,
      },
      '&:last-of-type': {
        borderTopRightRadius: theme.shape.borderRadius,
      },
    },
  },
  '& .MuiTableBody-root .MuiTableRow-root': {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '& .MuiTableCell-root': {
      padding: '12px 16px',
      borderBottom: `1px solid ${theme.palette.divider}`,
      fontSize: '0.95rem',
      verticalAlign: 'middle',
      color: theme.palette.text.primary,
    },
    '& .Mui-disabled, & .MuiTypography-colorTextSecondary': {
      color: `${theme.palette.text.secondary} !important`,
      opacity: 1,
    },
  },
  '& .MuiTableSortLabel-root': {
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.text.primary,
    },
    '&.Mui-active': {
      color: theme.palette.text.secondary,
    },
  },
  '& .editable-cell': {
    position: 'relative',
    '&:hover .edit-button': {
      opacity: 1,
    },
  },
  '& .edit-button': {
    opacity: 0,
    transition: 'opacity 0.2s',
    position: 'absolute',
    right: '4px',
    top: '50%',
    transform: 'translateY(-50%)',
  },
}));

const SortableTable = ({ 
  columns, 
  data, 
  title, 
  defaultSort = null,
  defaultOrder = 'asc',
  onRowClick = null,
  showIndex = false,
  editable = false,
  onEdit = null,
  onDelete = null,
  onAdd = null,
  addDialog = null,
  showActions = true,
  showPagination = true,
  defaultRowsPerPage = 5,
  permissions = {
    canEdit: true,
    canDelete: true,
    canAdd: true
  }
}) => {
  const { role } = useAuth();
  const [order, setOrder] = useState(defaultOrder);
  const [orderBy, setOrderBy] = useState(defaultSort);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);


  
  const sortedData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    if (!orderBy) return data;

    return [...data].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      // Handle nested object properties
      if (orderBy.includes('.')) {
        const keys = orderBy.split('.');
        aValue = keys.reduce((obj, key) => obj?.[key], a);
        bValue = keys.reduce((obj, key) => obj?.[key], b);
      }

      // Handle different data types
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      } else if (typeof aValue === 'number') {
        // Keep as is for numbers
      } else {
        aValue = String(aValue || '').toLowerCase();
        bValue = String(bValue || '').toLowerCase();
      }

      if (order === 'desc') {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      } else {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      }
    });
  }, [data, orderBy, order]);

  // Pagination logic
  const paginatedData = useMemo(() => {
    if (!showPagination || !sortedData) return sortedData || [];
    const startIndex = page * rowsPerPage;
    return sortedData.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedData, page, rowsPerPage, showPagination]);

  const totalPages = Math.ceil((sortedData || []).length / rowsPerPage);

  // Validate props AFTER ALL hooks
  if (!columns || !Array.isArray(columns)) {
    console.error('SortableTable: columns prop is missing or not an array:', columns);
    return <Box>Error: Invalid columns prop</Box>;
  }
  
  if (!data || !Array.isArray(data)) {
    console.error('SortableTable: data prop is missing or not an array:', data);
    return <Box>Error: Invalid data prop</Box>;
  }
  


  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getSortIcon = (columnId) => {
    if (orderBy !== columnId) {
      return <Sort />;
    }
    return order === 'desc' ? <ArrowDownward /> : <ArrowUpward />;
  };

  const handleEditStart = (rowIndex, columnId, value) => {
    setEditingCell({ rowIndex, columnId });
    setEditValue(value);
  };

  const handleEditSave = () => {
    if (onEdit && editingCell) {
      onEdit(editingCell.rowIndex, editingCell.columnId, editValue);
    }
    setEditingCell(null);
    setEditValue('');
  };

  const handleEditCancel = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const handleDelete = (rowIndex) => {
    if (onDelete) {
      onDelete(rowIndex);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when changing rows per page
  };

  const canEdit = editable && permissions.canEdit && (role === 'admin' || role === 'superadmin');
  const canDelete = editable && permissions.canDelete && (role === 'admin' || role === 'superadmin');
  const canAdd = editable && permissions.canAdd && (role === 'admin' || role === 'superadmin');



  const noDataAvailable = !data || data.length === 0;
  return (
    <Box sx={{ width: '100%' }}>
      {title && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {title}
          </Typography>
          {canAdd && onAdd && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddDialogOpen(true)}
              size="small"
            >
              Add New
            </Button>
          )}
        </Box>
      )}
      <StyledTableContainer component={Paper} elevation={2} sx={{ background: 'transparent', boxShadow: 'none' }}>
        <Table>
          <TableHead>
            <TableRow>
              {showIndex && (
                <TableCell align="center" sx={{ width: 60 }}>
                  #
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align || 'left'}
                  sortDirection={orderBy === column.id ? order : false}
                  sx={{ 
                    width: column.width,
                    cursor: column.sortable !== false ? 'pointer' : 'default'
                  }}
                  className={column.hideOnMobile ? 'hide-on-mobile' : ''}
                  onClick={() => column.sortable !== false && handleRequestSort(column.id)}
                >
                  {column.sortable !== false ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => handleRequestSort(column.id)}
                      IconComponent={() => getSortIcon(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
              {editable && (canDelete) && showActions && (
                <TableCell align="center" sx={{ width: 120 }}>
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {noDataAvailable ? (
              <TableRow>
                <TableCell colSpan={columns.length + (showIndex ? 1 : 0) + (editable && canDelete && showActions ? 1 : 0)} align="center" sx={{ color: '#ff1744', fontWeight: 700, fontSize: '1.1em', py: 3 }}>
                  No data available
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row, index) => {
                const actualIndex = page * rowsPerPage + index;
                return (
                  <TableRow
                    key={actualIndex}
                    hover
                    onClick={() => onRowClick && onRowClick(row, actualIndex)}
                    sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
                  >
                    {showIndex && (
                      <TableCell align="center" sx={{ fontWeight: 500 }}>
                        {actualIndex + 1}
                      </TableCell>
                    )}
                    {columns.map((column) => {
                      const isEditing = editingCell && 
                        editingCell.rowIndex === actualIndex && 
                        editingCell.columnId === column.id;
                      return (
                        <TableCell 
                          key={column.id} 
                          align={column.align || 'left'}
                          className={
                            (column.editable ? 'editable-cell ' : '') +
                            (column.hideOnMobile ? 'hide-on-mobile' : '')
                          }
                          sx={{ position: 'relative' }}
                        >
                          {isEditing ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {column.type === 'select' ? (
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                  <Select
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    autoFocus
                                  >
                                    {column.options?.map((option) => (
                                      <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              ) : (
                                <TextField
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  size="small"
                                  autoFocus
                                  type={column.type || 'text'}
                                  inputProps={column.inputProps}
                                />
                              )}
                              <IconButton size="small" onClick={handleEditSave} color="primary">
                                <SaveIcon />
                              </IconButton>
                              <IconButton size="small" onClick={handleEditCancel} color="error">
                                <CancelIcon />
                              </IconButton>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <span>
                                {column.render ? column.render(row[column.id], row, actualIndex) : row[column.id]}
                              </span>
                              {column.editable && canEdit && (
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditStart(actualIndex, column.id, row[column.id]);
                                    }}
                                    className="edit-button"
                                    sx={{ ml: 1 }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          )}
                        </TableCell>
                      );
                    })}
                    {editable && (canDelete) && showActions && (
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          {canDelete && (
                            <Tooltip title="Delete Row">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(actualIndex);
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>


                                                       {/* Pagination Controls */}
         {showPagination && data.length > 0 && (
           <Box sx={{
             display: 'flex',
             flexDirection: 'row',
             flexWrap: 'nowrap',
             alignItems: 'center',
             justifyContent: 'space-between',
             mt: 1,
             px: 1,
             py: 0.5,
             background: (theme) => theme.palette.background.paper,
             borderTop: (theme) => `1px solid ${theme.palette.divider}`,
             gap: 1,
             '@media (max-width: 600px)': {
               px: 0.5,
               gap: 0.5,
             },
           }}>
             <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap' }}>
               <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.60rem', md: '0.875rem' } }}>
                 Rows
               </Typography>
               <FormControl size="small" sx={{ minWidth: 36 }}>
                 <Select
                   value={rowsPerPage}
                   onChange={handleRowsPerPageChange}
                   displayEmpty
                   sx={{ height: 20, fontSize: { xs: '0.60rem', md: '0.875rem' } }}
                 >
                   <MenuItem value={5}>5</MenuItem>
                   <MenuItem value={10}>10</MenuItem>
                   <MenuItem value={25}>25</MenuItem>
                   <MenuItem value={50}>50</MenuItem>
                 </Select>
               </FormControl>
               <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.60rem', md: '0.875rem' } }}>
                 {`${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, sortedData.length)} of ${sortedData.length}`}
               </Typography>
             </Box>
             <Pagination
               count={totalPages}
               page={page + 1}
               onChange={handlePageChange}
               showFirstButton
               showLastButton
               size="small"
               sx={{
                 '& .MuiPaginationItem-root': {
                   fontSize: { xs: '0.60rem', md: '0.875rem' },
                   minWidth: 18,
                   height: 16,
                   m: 0,
                   p: 0,
                 }
               }}
             />
           </Box>
         )}

      {/* Add Dialog */}
      {addDialog && (
        <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add New Entry</DialogTitle>
          <DialogContent>
            {addDialog}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
            <Button onClick={() => {
              if (onAdd) onAdd();
              setAddDialogOpen(false);
            }} variant="contained">
              Add
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default SortableTable;
